import React, { useState, useEffect, useRef } from 'react';
import {
  fetchAllPromoVideos,
  createPromoVideo,
  updatePromoVideo,
  deletePromoVideo,
  togglePromoVideoVisibility,
  uploadPromoVideoFile,
  uploadPromoThumbnail,
} from '../../../services/promoVideoService';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Upload, Video, Image,
  Calendar, ToggleLeft, ToggleRight, RefreshCw, X, Save, ChevronUp, ChevronDown, Clock
} from 'lucide-react';

const CATEGORIES = ['Promotion', 'Activité', 'Événement', 'Présentation'];

const emptyForm = {
  title: '',
  description: '',
  category: 'Promotion',
  videoUrl: '',
  thumbnailUrl: '',
  duration: '',
  isVisible: true,
  displayOrder: 0,
  scheduledAt: '',
};

const PromoVideoAdminPanel = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllPromoVideos();
      setVideos(data);
    } catch (err) {
      setError('Erreur lors du chargement des vidéos: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const openCreate = () => {
    setEditingVideo(null);
    setForm({ ...emptyForm, displayOrder: videos?.length + 1 });
    setShowForm(true);
  };

  const openEdit = (video) => {
    setEditingVideo(video);
    setForm({
      title: video?.title || '',
      description: video?.description || '',
      category: video?.category || 'Promotion',
      videoUrl: video?.video_url || '',
      thumbnailUrl: video?.thumbnail_url || '',
      duration: video?.duration || '',
      isVisible: video?.is_visible !== false,
      displayOrder: video?.display_order || 0,
      scheduledAt: video?.scheduled_at ? video?.scheduled_at?.slice(0, 16) : '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingVideo(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleVideoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      setUploadingVideo(true);
      const { publicUrl } = await uploadPromoVideoFile(file);
      setForm(prev => ({ ...prev, videoUrl: publicUrl }));
    } catch (err) {
      setError('Erreur upload vidéo: ' + err?.message);
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      setUploadingThumb(true);
      const { publicUrl } = await uploadPromoThumbnail(file);
      setForm(prev => ({ ...prev, thumbnailUrl: publicUrl }));
    } catch (err) {
      setError('Erreur upload miniature: ' + err?.message);
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleSave = async () => {
    if (!form?.title?.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        scheduledAt: form?.scheduledAt ? new Date(form.scheduledAt)?.toISOString() : null,
      };
      if (editingVideo) {
        await updatePromoVideo(editingVideo?.id, payload);
      } else {
        await createPromoVideo(payload);
      }
      await loadVideos();
      closeForm();
    } catch (err) {
      setError('Erreur lors de la sauvegarde: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (video) => {
    try {
      await togglePromoVideoVisibility(video?.id, !video?.is_visible);
      setVideos(prev => prev?.map(v => v?.id === video?.id ? { ...v, is_visible: !v?.is_visible } : v));
    } catch (err) {
      setError('Erreur toggle visibilité: ' + err?.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePromoVideo(id);
      setVideos(prev => prev?.filter(v => v?.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Erreur suppression: ' + err?.message);
    }
  };

  const handleReorder = async (video, direction) => {
    const idx = videos?.findIndex(v => v?.id === video?.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= videos?.length) return;
    const swapVideo = videos?.[swapIdx];
    try {
      await updatePromoVideo(video?.id, { displayOrder: swapVideo?.display_order });
      await updatePromoVideo(swapVideo?.id, { displayOrder: video?.display_order });
      await loadVideos();
    } catch (err) {
      setError('Erreur réorganisation: ' + err?.message);
    }
  };

  const categoryBadgeColor = (cat) => {
    if (cat === 'Promotion') return 'bg-yellow-100 text-yellow-800';
    if (cat === 'Activité') return 'bg-blue-100 text-blue-800';
    if (cat === 'Événement') return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vidéos Promotionnelles</h3>
          <p className="text-sm text-gray-500 mt-1">Gérez les vidéos affichées dans la section "Vidéos & Activités" de la page À propos</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadVideos}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter une vidéo
          </button>
        </div>
      </div>
      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <X className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h4 className="text-lg font-semibold text-gray-900">
                {editingVideo ? 'Modifier la vidéo' : 'Ajouter une vidéo'}
              </h4>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={form?.title}
                  onChange={e => handleFormChange('title', e?.target?.value)}
                  placeholder="Ex: Visite Exclusive — Cocody Riviera"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form?.description}
                  onChange={e => handleFormChange('description', e?.target?.value)}
                  rows={3}
                  placeholder="Courte description de la vidéo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Category + Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={form?.category}
                    onChange={e => handleFormChange('category', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durée</label>
                  <input
                    type="text"
                    value={form?.duration}
                    onChange={e => handleFormChange('duration', e?.target?.value)}
                    placeholder="Ex: 2:45"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Video Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier vidéo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form?.videoUrl}
                    onChange={e => handleFormChange('videoUrl', e?.target?.value)}
                    placeholder="URL de la vidéo ou uploader un fichier"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef?.current?.click()}
                    disabled={uploadingVideo}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {uploadingVideo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingVideo ? 'Upload...' : 'Upload'}
                  </button>
                  <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
                </div>
                {form?.videoUrl && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Video className="w-3 h-3" /> Vidéo définie
                  </p>
                )}
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Miniature (thumbnail)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form?.thumbnailUrl}
                    onChange={e => handleFormChange('thumbnailUrl', e?.target?.value)}
                    placeholder="URL de la miniature ou uploader une image"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => thumbInputRef?.current?.click()}
                    disabled={uploadingThumb}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {uploadingThumb ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                    {uploadingThumb ? 'Upload...' : 'Image'}
                  </button>
                  <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} />
                </div>
                {form?.thumbnailUrl && (
                  <div className="mt-2">
                    <img src={form?.thumbnailUrl} alt="Miniature" className="h-16 w-28 object-cover rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>

              {/* Schedule + Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> Planifier la publication
                  </label>
                  <input
                    type="datetime-local"
                    value={form?.scheduledAt}
                    onChange={e => handleFormChange('scheduledAt', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
                  <input
                    type="number"
                    min="0"
                    value={form?.displayOrder}
                    onChange={e => handleFormChange('displayOrder', parseInt(e?.target?.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Visibility Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Visibilité</p>
                  <p className="text-xs text-gray-500 mt-0.5">Afficher cette vidéo sur la page À propos</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleFormChange('isVisible', !form?.isVisible)}
                  className="flex items-center gap-2"
                >
                  {form?.isVisible
                    ? <ToggleRight className="w-8 h-8 text-blue-600" />
                    : <ToggleLeft className="w-8 h-8 text-gray-400" />
                  }
                  <span className={`text-sm font-medium ${form?.isVisible ? 'text-blue-600' : 'text-gray-400'}`}>
                    {form?.isVisible ? 'Visible' : 'Masquée'}
                  </span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
              <button onClick={closeForm} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Supprimer la vidéo</h4>
            <p className="text-sm text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>"{deleteConfirm?.title}"</strong> ? Cette action est irréversible.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm?.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : videos?.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucune vidéo pour l'instant</p>
          <p className="text-gray-400 text-sm mt-1">Cliquez sur "Ajouter une vidéo" pour commencer</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ordre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Vidéo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Catégorie</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Planification</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visibilité</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {videos?.map((video, idx) => (
                <tr key={video?.id} className="hover:bg-gray-50 transition-colors">
                  {/* Order controls */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        onClick={() => handleReorder(video, 'up')}
                        disabled={idx === 0}
                        className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                      <span className="text-xs font-mono text-gray-400">{video?.display_order}</span>
                      <button
                        onClick={() => handleReorder(video, 'down')}
                        disabled={idx === videos?.length - 1}
                        className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    </div>
                  </td>

                  {/* Video info */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {video?.thumbnail_url
                          ? <img src={video?.thumbnail_url} alt={video?.title} className="w-full h-full object-cover" />
                          : <Video className="w-4 h-4 text-gray-400" />
                        }
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm leading-tight">{video?.title}</p>
                        {video?.description && (
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{video?.description}</p>
                        )}
                        {video?.video_url && (
                          <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                            <Video className="w-3 h-3" /> Vidéo disponible
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${categoryBadgeColor(video?.category)}`}>
                      {video?.category}
                    </span>
                  </td>

                  {/* Duration */}
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {video?.duration || '—'}
                  </td>

                  {/* Schedule */}
                  <td className="px-4 py-3">
                    {video?.scheduled_at ? (
                      <div className="flex items-center gap-1 text-xs text-blue-600">
                        <Clock className="w-3 h-3" />
                        {new Date(video.scheduled_at)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Immédiat</span>
                    )}
                  </td>

                  {/* Visibility toggle */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleVisibility(video)}
                      className="flex items-center gap-1.5 group"
                      title={video?.is_visible ? 'Masquer' : 'Afficher'}
                    >
                      {video?.is_visible
                        ? <><Eye className="w-4 h-4 text-green-600" /><span className="text-xs text-green-600 font-medium">Visible</span></>
                        : <><EyeOff className="w-4 h-4 text-gray-400" /><span className="text-xs text-gray-400">Masquée</span></>
                      }
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(video)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(video)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-red-500"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Stats footer */}
      {videos?.length > 0 && (
        <div className="flex items-center gap-6 text-sm text-gray-500">
          <span>{videos?.length} vidéo{videos?.length > 1 ? 's' : ''} au total</span>
          <span className="text-green-600">{videos?.filter(v => v?.is_visible)?.length} visible{videos?.filter(v => v?.is_visible)?.length > 1 ? 's' : ''}</span>
          <span className="text-gray-400">{videos?.filter(v => !v?.is_visible)?.length} masquée{videos?.filter(v => !v?.is_visible)?.length > 1 ? 's' : ''}</span>
          {videos?.filter(v => v?.scheduled_at)?.length > 0 && (
            <span className="text-blue-600">{videos?.filter(v => v?.scheduled_at)?.length} planifiée{videos?.filter(v => v?.scheduled_at)?.length > 1 ? 's' : ''}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default PromoVideoAdminPanel;
